import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  Stack,
  Paper,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import api from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

const SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPT_IMAGES = 'image/png,image/jpeg,image/jpg,image/gif,image/webp';

const Support: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScreenshotError(null);
    const f = e.target.files?.[0];
    if (!f) {
      setScreenshot(null);
      return;
    }
    if (f.size > SCREENSHOT_MAX_BYTES) {
      setScreenshotError('Screenshot must not exceed 10MB.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowed.includes(f.type)) {
      setScreenshotError('Screenshot must be PNG, JPEG, GIF, or WebP.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setScreenshot(f);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    setScreenshotError(null);

    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setError('Please fill in all required fields.');
      setSubmitting(false);
      return;
    }

    if (message.trim().length < 10) {
      setError('Please provide more details about the issue (at least 10 characters).');
      setSubmitting(false);
      return;
    }

    if (screenshot && screenshot.size > SCREENSHOT_MAX_BYTES) {
      setScreenshotError('Screenshot must not exceed 10MB.');
      setSubmitting(false);
      return;
    }

    try {
      const form = new FormData();
      form.append('name', name.trim());
      form.append('email', email.trim());
      form.append('subject', subject.trim());
      form.append('message', message.trim());
      if (screenshot) form.append('screenshot', screenshot);

      await api.post('/support/public', form, {
        headers: { 'Content-Type': undefined },
      });

      setSuccess('Your support request has been submitted. We will get back to you as soon as possible.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f4f7fb', minHeight: '100vh', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Link
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 600 }}
          >
            <ArrowBack fontSize="small" /> Back to main page
          </Link>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Support
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Having an issue? Let us know what's wrong and we'll help you resolve it.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} sx={{ mx: 'auto' }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Your Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Email Address"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          fullWidth
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      label="Subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      fullWidth
                      inputProps={{ maxLength: 100 }}
                      helperText={`${subject.length}/100 characters`}
                    />

                    <TextField
                      label="Description"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      fullWidth
                      multiline
                      rows={6}
                      inputProps={{ maxLength: 2000 }}
                      helperText={`${message.length}/2000 characters`}
                      placeholder="Please provide details about the issue you're experiencing..."
                    />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Screenshot (optional, max 10MB)
                      </Typography>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT_IMAGES}
                        onChange={handleScreenshotChange}
                        style={{ display: 'block', marginBottom: 4 }}
                      />
                      {screenshot && (
                        <Typography variant="caption" color="text.secondary">
                          Selected: {screenshot.name} ({(screenshot.size / 1024).toFixed(1)} KB)
                        </Typography>
                      )}
                      {screenshotError && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {screenshotError}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/')}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting}
                        sx={{ minWidth: 120 }}
                      >
                        {submitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </CardContent>
            </Card>

            <Paper sx={{ mt: 3, p: 3, backgroundColor: '#f0f8ff', border: '1px solid #d0e7ff' }}>
              <Typography variant="body2" color="text.secondary" align="center">
                <strong>Need urgent assistance?</strong> Please contact us directly at{' '}
                <a href="mailto:support@workflow.estate" style={{ color: '#007AFF' }}>
                  support@workflow.estate
                </a>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Support;
